import { useEffect, useMemo } from "react";
import { updateStateValues, setSelectedRows, selectSingleRow } from "./actions";
import { isFunction, flattenColumns, getMaxHeaderCount } from "./utils";
import { useTable } from "./context";

function useColumnsFromColumnsDef(columnsDef, responsive) {
  return useMemo(() => {
    if (!columnsDef) {
      return { columns: [] };
    }
    let columns = flattenColumns(columnsDef, responsive);
    const maxHeaderCount = getMaxHeaderCount(columns);

    if (responsive) {
      columns = columns.map(column => ({ ...column, width: 0 }));
    }

    return { columns, maxHeaderCount };
  }, [columnsDef, responsive]);
}

function useUpdateContextOnPropChanges(fireAction, props, contextValue) {
  const { controlSelection, selectedRows, selectedRow, uniqueDataKey, data } =
    contextValue;

  useEffect(() => {
    fireAction(updateStateValues({ ...props }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fireAction,
    props.tableId,
    props.responsive,
    props.children,
    props.data,
    props.width,
    props.height,
    props.uniqueDataKey,
    props.onSelectAll,
    props.onRowClick,
    props.getSelectionInputTooltip,
    props.getRowClassName,
    props.hideHeader,
    props.fixSelectionColumn,
    props.noCheckboxTable,
    props.isExpandable,
    props.isSelectable,
    props.multiSelect,
    props.disabledRows,
    props.onRowSelectionChange,
    props.isLoading,
    props.isLoadingMore,
    props.loadingMoreText,
    props.onLoadMore,
    props.loadedAll,
    props.rowRenderer,
    props.emptyRenderer,
    props.footerRenderer,
    props.footerHeight,
    props.estimatedRowHeight,
    props.enableFullScreen,
    props.exportToCsv,
    props.csvFileName,
    props.sortBy,
    props.sortOrder,
    props.onSort,
    props.exportPopupMaxHeight,
    props.defaultExpandedRows,
    props.expandedRows,
    props.onRowExpand,
    props.enableCSVToastMessage
  ]);

  useEffect(() => {
    const allRowIds = data ? data.map(obj => obj[uniqueDataKey]) : [];

    if (!controlSelection && selectedRows.length > 0) {
      const currentRows = selectedRows.filter(rowId =>
        allRowIds.includes(rowId)
      );
      if (selectedRows.length !== currentRows.length) {
        fireAction(setSelectedRows(currentRows));
      }
    }

    if (
      !controlSelection &&
      selectedRow !== null &&
      !allRowIds.includes(selectedRow)
    ) {
      selectSingleRow(null);
    }
  }, [
    data,
    controlSelection,
    selectedRows,
    selectedRow,
    uniqueDataKey,
    fireAction
  ]);
}

function useRowSelect(
  {
    fireAction,
    selectedRows: stateSelectedRows,
    selectedRow: stateSelectedRow
  },
  { onRowSelectionChange, noCheckboxTable, multiSelect, selectedRows }
) {
  useEffect(() => {
    const selectionState = {
      controlSelection: !!selectedRows
    };

    if (selectedRows) {
      if (multiSelect) {
        selectionState.selectedRows = selectedRows;
      } else {
        selectionState.selectedRow = selectedRows[0] || null;
      }
    }

    fireAction(updateStateValues(selectionState));
  }, [fireAction, selectedRows, multiSelect]);

  useEffect(() => {
    if (isFunction(onRowSelectionChange) && !noCheckboxTable) {
      onRowSelectionChange(multiSelect ? stateSelectedRows : stateSelectedRow);
    }
  }, [
    onRowSelectionChange,
    noCheckboxTable,
    multiSelect,
    stateSelectedRows,
    stateSelectedRow
  ]);
}

function useTableState() {
  const { selectedRows, disabledRows, data, uniqueDataKey, selectedRow } =
    useTable();
  const totalRows = data.length;
  const selectedRowIds = selectedRows;

  const totalSelectedRows = selectedRows.length;
  const disabledRowIds = disabledRows;

  const totalDisabledRows = disabledRows.length;

  const {
    unSelectedRowIds,
    unSelectedRowItems,
    selectedRowItems,
    disabledRowItems,
    totalUnselectedRows,
    selectedRowItem
  } = useMemo(() => {
    /* eslint-disable */
    const selectedRowItems = [];
    const disabledRowItems = [];
    const unSelectedRowIds = [];
    const unSelectedRowItems = [];
    let selectedRowItem = null;
    /* eslint-enable */
    data.forEach(record => {
      const id = record[uniqueDataKey];

      if (!selectedRows.includes(id)) {
        unSelectedRowIds.push(id);
        unSelectedRowItems.push(record);
      } else {
        selectedRowItems.push(record);
      }

      if (disabledRows.includes(id)) {
        disabledRowItems.push(record);
      }

      if (id === selectedRow) {
        selectedRowItem = record;
      }
    });

    return {
      selectedRowItems,
      disabledRowItems,
      unSelectedRowIds,
      unSelectedRowItems,
      selectedRowItem,
      totalUnselectedRows: unSelectedRowIds.length
    };
  }, [data, disabledRows, selectedRows, uniqueDataKey, selectedRow]);

  return {
    totalRows,
    selectedRowId: selectedRow,
    selectedRowItem,
    selectedRowIds,
    selectedRowItems,
    totalSelectedRows,
    disabledRowIds,
    disabledRowItems,
    totalDisabledRows,
    unSelectedRowIds,
    unSelectedRowItems,
    totalUnselectedRows
  };
}

export {
  useTableState,
  useColumnsFromColumnsDef,
  useRowSelect,
  useUpdateContextOnPropChanges
};
